import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/joy";
import { ArrowLeft, Camera, ImageUp } from "lucide-react";

import { AuthenticatedImage } from "..";
import { MobileImagePreview } from "./MobileImagePreview";
import { ImageUploadModal } from "./ImageUploadModal";
import { AlbumGroup } from "./types";
import { useSingleAlbum } from "./hooks";
import { getDisplayAssetId, getDisplayUrl, isPendingUpload } from "../../utils/taskAssetImages";
import { AlbumImageUploadOverlay } from "./AlbumImageUploadOverlay";

interface MobileGalleryOverlayProps {
    group: AlbumGroup | null;
    taskId: string;
    projectId: string;
    onClose: () => void;
    onImagesChange: (images: AlbumGroup["images"]) => void;
}

export function MobileGalleryOverlay(props: MobileGalleryOverlayProps) {
    const { group, taskId, projectId, onClose, onImagesChange } = props;
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const {
        uploadModalOpen,
        setUploadModalOpen,
        pendingFiles,
        setPendingFiles,
        fileInputRef,
        openFilePicker,
        handleFileInput,
        handleUploadConfirm,
        isUploading,
    } = useSingleAlbum({
        label: group?.label || "",
        taskId,
        projectId,
        images: group?.images || [],
        onImagesChange,
    });

    useEffect(() => {
        if (!group) {
            return;
        }

        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [group?.id]);

    if (!group) {
        return null;
    }

    const photoCount = group.images.length;

    return createPortal(
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 1300,
                bgcolor: "background.body",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                }}
            >
                <IconButton
                    variant="plain"
                    color="neutral"
                    onClick={onClose}
                    sx={{ borderRadius: "4px" }}
                >
                    <ArrowLeft size={20} />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-md" noWrap>
                        {group.title}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }} noWrap>
                        {photoCount} photo{photoCount !== 1 ? "s" : ""}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                        {group.description}
                    </Typography>
                </Box>

                {photoCount > 0 ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 0.5,
                            px: 0.5,
                            pb: 2,
                        }}
                    >
                        {group.images.map((image, index) => (
                            <Box
                                key={`${group.id}-${index}`}
                                onClick={() => {
                                    if (!isPendingUpload(image)) {
                                        setPreviewIndex(index);
                                    }
                                }}
                                sx={{
                                    position: "relative",
                                    aspectRatio: "1/1",
                                    bgcolor: "background.level2",
                                    overflow: "hidden",
                                    cursor: isPendingUpload(image) ? "default" : "pointer",
                                    "&:active": {
                                        opacity: isPendingUpload(image) ? 1 : 0.75,
                                    },
                                }}
                            >
                                <AuthenticatedImage
                                    assetId={getDisplayAssetId(image)}
                                    url={getDisplayUrl(image)}
                                    alt={`Photo ${index + 1}`}
                                    borderRadius={0}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                                {isPendingUpload(image) && <AlbumImageUploadOverlay />}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            mx: 2,
                            my: 2,
                            p: 3,
                            borderRadius: "4px",
                            border: "1px dashed",
                            borderColor: "divider",
                            bgcolor: "background.level1",
                            textAlign: "center",
                        }}
                    >
                        <Camera size={28} style={{ opacity: 0.45 }} />
                        <Typography level="body-sm" sx={{ mt: 1, color: "text.secondary" }}>
                            No photos yet. Tap upload to add images for this category.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                    pb: "max(12px, env(safe-area-inset-bottom))",
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif,.heic,.heif"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileInput}
                />
                <Button
                    variant="solid"
                    color="primary"
                    fullWidth
                    size="lg"
                    startDecorator={<ImageUp size={18} />}
                    onClick={openFilePicker}
                    disabled={isUploading}
                    sx={{ borderRadius: "4px", fontWeight: 600 }}
                >
                    Upload Photos
                </Button>
            </Box>

            {previewIndex !== null && (
                <MobileImagePreview
                    images={group.images}
                    index={previewIndex}
                    onClose={() => setPreviewIndex(null)}
                    onIndexChange={setPreviewIndex}
                />
            )}
            <ImageUploadModal
                open={uploadModalOpen}
                files={pendingFiles}
                onClose={() => {
                    setUploadModalOpen(false);
                    setPendingFiles([]);
                }}
                onUpload={handleUploadConfirm}
            />
        </Box>,
        document.body,
    );
}
